import type { ColumnsType } from "antd/lib/table";
import { Button, Table } from "antd";
import { useIntl } from "react-intl";
import { IInitiativeListItem } from "../interfaces/IInitiativeListItem";
import { useQueryParams } from "use-query-params";
import { useColumnFactory } from "@/hooks";
import { GetDropDownFilterProps } from "@/components/TableFilters/components/GetDropDownFilterProps";
import { useGetInitiativeFilterableColumnOptionsQuery } from "@/services";
import { INITIATIVE_DEFAULT_QUERY_PARAMS } from "../constants";
import { routes } from "@/helpers";
import { Link } from "react-router-dom";

interface IProps {
  data: IInitiativeListItem[] | undefined;
  isFetching: boolean;
}
export const InitiativeTable = ({ data: initiatives, isFetching }: IProps) => {
  const [queryParams, setQueryParams] = useQueryParams({
    ...INITIATIVE_DEFAULT_QUERY_PARAMS
  });

  const updateQueryParam = (key: string, value: unknown) => {
    let page = queryParams.page || 1;

    if (key !== "page") {
      page = 1;
    }

    setQueryParams({ [key]: value, page });
  };

  const { createSortableDateColumn, handleChange } = useColumnFactory(
    queryParams,
    setQueryParams
  );
  const intl = useIntl();

  const { data: filterableColumnOptions } = useGetInitiativeFilterableColumnOptionsQuery(null, { refetchOnMountOrArgChange: true });

  const statusOptions: { id: string; fallback: string }[] = filterableColumnOptions?.initiativeStatusOptions.map(option => ({ id: option.id, fallback: option.name })) || [];
  const productCategoryOptions: { id: string; fallback: string }[] = filterableColumnOptions?.productCategoryOptions.map(option => ({ id: option.id, fallback: option.name })) || [];
  const projectTypeOptions: { id: string; fallback: string }[] = filterableColumnOptions?.projectTypeOptions.map(option => ({ id: option.id, fallback: option.name })) || [];


  const columns: ColumnsType<IInitiativeListItem> = [
    {
      title: intl.formatMessage({ id: "initiative.columns.name" }),
      dataIndex: "name",
      render: (name, record) => (
        <Button data-test-id={`initiative-table-link-${record.publicId}`} type="link">
          <Link to={routes.initiatives.edit(record.publicId)}>{name || "n/a"}</Link>
        </Button>
      ),
    },
    {
      title: intl.formatMessage({ id: "initiative.columns.status" }),
      dataIndex: "status",
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: "status",
        queryParamValue: queryParams.status,
        singleChoice: true,
        options: statusOptions
      }),
    },
    {
      title: intl.formatMessage({ id: "projects.productCategory" }),
      dataIndex: "productCategory",
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: "productCategoryIds",
        queryParamValue: queryParams.productCategoryIds,
        options: productCategoryOptions
      })
    },
    createSortableDateColumn(
      intl.formatMessage({ id: "initiative.estimatedTargetLaunchDate" }),
      "estimatedTargetLaunchDate"
    ),
    {
      title: intl.formatMessage({ id: "initiative.columns.projectType" }),
      dataIndex: "projectType",
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: "projectTypeIds",
        queryParamValue: queryParams.projectTypeIds,
        options: projectTypeOptions
      })
    },
  ];

  return (
    <Table
      data-test-id="initiatives-table"
      columns={columns}
      dataSource={initiatives}
      loading={isFetching}
      onChange={handleChange}
      rowKey="publicId"
      pagination={false}
    />
  );
};